<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  recipientCount: {
    type: Number,
    default: 0,
  },
  inboxCount: {
    type: Number,
    default: 0,
  },
  messageType: {
    type: String,
    default: 'Texto',
  },
  estimatedTime: {
    type: String,
    default: null,
  },
  availableLabels: {
    type: Array,
    default: () => [],
  },
  isConfirming: {
    type: Boolean,
    default: false,
  },
  disconnectedInboxWarnings: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'close',
  'confirm',
  'create-label',
  'show-add-label-modal',
]);

const selectedLabels = ref([]);
const selectedRemoveLabels = ref([]);
const showLabelDropdown = ref(false);
const showRemoveLabelDropdown = ref(false);
const searchQuery = ref('');
const removeSearchQuery = ref('');
const isCreatingLabel = ref(false);
const dropdownRef = ref(null);
const dropdownContainer = ref(null);
const removeDropdownContainer = ref(null);
const dropdownPosition = ref('bottom');
const removeDropdownPosition = ref('bottom');

// IA Automática state
const atendimentoAction = ref('none'); // 'none' | 'add' | 'remove'

// Options for the segmented control
const atendimentoOptions = [
  { id: 'none', label: 'Não alterar', icon: 'i-lucide-minus' },
  { id: 'remove', label: 'Ativar IA', icon: 'i-lucide-bot' },
  { id: 'add', label: 'Atendimento', icon: 'i-lucide-user' },
];

// Computed for descriptive text based on selection
const atendimentoDescription = computed(() => {
  switch (atendimentoAction.value) {
    case 'remove':
      return `A IA será ativada para ${props.recipientCount} contatos. As respostas automáticas serão habilitadas.`;
    case 'add':
      return `A IA será desativada para ${props.recipientCount} contatos. Apenas atendentes humanos responderão.`;
    default:
      return 'A configuração da IA não será alterada.';
  }
});

// Filter labels based on search query (excluding "atendimento-humano" - handled by IA Automática section)
const filteredLabels = computed(() => {
  const labelsWithoutAtendimento = props.availableLabels.filter(
    label => label.title.toLowerCase() !== 'atendimento-humano'
  );

  if (!searchQuery.value) return labelsWithoutAtendimento;

  const query = searchQuery.value.toLowerCase();
  return labelsWithoutAtendimento.filter(label =>
    label.title.toLowerCase().includes(query)
  );
});

// Check if search query matches any existing label
const isNewLabel = computed(() => {
  if (!searchQuery.value.trim()) return false;

  const query = searchQuery.value.toLowerCase().trim();
  return !props.availableLabels.some(
    label => label.title.toLowerCase() === query
  );
});

// Filter remove labels (excluding "atendimento-humano" - handled by IA Automática section)
const filteredRemoveLabels = computed(() => {
  const labelsWithoutAtendimento = props.availableLabels.filter(
    label => label.title.toLowerCase() !== 'atendimento-humano'
  );

  if (!removeSearchQuery.value) return labelsWithoutAtendimento;

  const query = removeSearchQuery.value.toLowerCase();
  return labelsWithoutAtendimento.filter(label =>
    label.title.toLowerCase().includes(query)
  );
});

const toggleLabel = label => {
  const index = selectedLabels.value.findIndex(l => l.id === label.id);
  if (index === -1) {
    selectedLabels.value.push(label);
  } else {
    selectedLabels.value.splice(index, 1);
  }
};

const isLabelSelected = label => {
  return selectedLabels.value.some(l => l.id === label.id);
};

const clearSelection = () => {
  selectedLabels.value = [];
};

const clearSearch = () => {
  searchQuery.value = '';
};

const toggleRemoveLabel = label => {
  const index = selectedRemoveLabels.value.findIndex(l => l.id === label.id);
  if (index === -1) {
    selectedRemoveLabels.value.push(label);
  } else {
    selectedRemoveLabels.value.splice(index, 1);
  }
};

const isRemoveLabelSelected = label => {
  return selectedRemoveLabels.value.some(l => l.id === label.id);
};

const clearRemoveSelection = () => {
  selectedRemoveLabels.value = [];
};

const clearRemoveSearch = () => {
  removeSearchQuery.value = '';
};

const confirmBroadcast = () => {
  emit('confirm', selectedLabels.value, selectedRemoveLabels.value, atendimentoAction.value);
};

const createNewLabel = async () => {
  if (!searchQuery.value.trim() || !isNewLabel.value) return;

  isCreatingLabel.value = true;

  // Generate a random color for the new label
  const colors = [
    '#1f93ff',
    '#f97316',
    '#0ea5e9',
    '#6366f1',
    '#8b5cf6',
    '#ec4899',
    '#f43f5e',
    '#10b981',
    '#f59e0b',
    '#ef4444',
  ];
  const randomColor = colors[Math.floor(Math.random() * colors.length)];

  const newLabel = {
    title: searchQuery.value.trim(),
    color: randomColor,
  };

  // Emit event to parent to create the label
  emit('create-label', newLabel);

  // Clear search after creating
  searchQuery.value = '';
  isCreatingLabel.value = false;
};

const showCreateLabelDialog = () => {
  // Close the dropdown first
  showLabelDropdown.value = false;

  // Emit event to parent to show the AddLabel modal
  emit('show-add-label-modal');
};

// Close dropdown when modal is closed and reset state
watch(
  () => props.show,
  newValue => {
    if (!newValue) {
      showLabelDropdown.value = false;
      searchQuery.value = '';
      showRemoveLabelDropdown.value = false;
      removeSearchQuery.value = '';
      atendimentoAction.value = 'none';
    }
  }
);

// Clear search when dropdown is closed
watch(showLabelDropdown, newValue => {
  if (!newValue) {
    searchQuery.value = '';
  } else {
    // Calculate dropdown position when opening
    nextTick(() => {
      calculateDropdownPosition();
    });
  }
});

watch(showRemoveLabelDropdown, newValue => {
  if (!newValue) {
    removeSearchQuery.value = '';
  } else {
    // Calculate dropdown position when opening
    nextTick(() => {
      calculateRemoveDropdownPosition();
    });
  }
});

// Toggle dropdown and calculate position
const toggleDropdown = () => {
  showLabelDropdown.value = !showLabelDropdown.value;
  if (showLabelDropdown.value) {
    nextTick(() => {
      calculateDropdownPosition();
    });
  }
};

const toggleRemoveDropdown = () => {
  showRemoveLabelDropdown.value = !showRemoveLabelDropdown.value;
  if (showRemoveLabelDropdown.value) {
    nextTick(() => {
      calculateRemoveDropdownPosition();
    });
  }
};

// Calculate if dropdown should open upward or downward
const calculateDropdownPosition = () => {
  if (!dropdownContainer.value) return;

  const buttonRect = dropdownContainer.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const dropdownHeight = 400; // Estimated max height
  const spaceBelow = viewportHeight - buttonRect.bottom;
  const spaceAbove = buttonRect.top;

  // If not enough space below and more space above, show dropdown above
  if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
    dropdownPosition.value = 'top';
  } else {
    dropdownPosition.value = 'bottom';
  }
};

const calculateRemoveDropdownPosition = () => {
  if (!removeDropdownContainer.value) return;

  const buttonRect = removeDropdownContainer.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const dropdownHeight = 400; // Estimated max height
  const spaceBelow = viewportHeight - buttonRect.bottom;
  const spaceAbove = buttonRect.top;

  if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
    removeDropdownPosition.value = 'top';
  } else {
    removeDropdownPosition.value = 'bottom';
  }
};

// Close dropdown when clicking outside
const handleClickOutside = event => {
  const containers = [dropdownContainer.value, removeDropdownContainer.value];
  const clickedInside = containers.some(
    container => container && container.contains(event.target)
  );

  if (!clickedInside) {
    showLabelDropdown.value = false;
    showRemoveLabelDropdown.value = false;
  }
};

// Add/remove click outside listener
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/20 dark:bg-n-slate-1/70 p-4 overflow-y-auto"
    >
      <div
        class="bg-n-solid-1 dark:bg-n-solid-1 rounded-xl w-full max-w-lg shadow-xl border border-n-slate-6 dark:border-n-slate-6 my-8 max-h-[90vh] flex flex-col animate-in fade-in-0 zoom-in-95 duration-300"
      >
        <!-- Modal Header -->
        <div
          class="flex items-center justify-between p-6 border-b border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-t-xl flex-shrink-0"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-600 dark:to-primary-800 rounded-lg flex items-center justify-center shadow-md"
            >
              <i
                class="i-lucide-send text-white text-xl"
              />
            </div>
            <h3
              class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12"
            >
              Confirmar Disparo
            </h3>
          </div>
          <button
            class="text-n-slate-11 dark:text-n-slate-11 p-2 hover:bg-n-slate-3 dark:hover:bg-n-slate-3 hover:text-n-slate-12 dark:hover:text-n-slate-12 rounded-lg transition-all duration-200"
            @click="$emit('close')"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>

        <!-- Modal Content -->
        <div class="p-6 flex-1 overflow-y-auto">
          <!-- Broadcast Summary -->
          <div class="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-50 dark:to-primary-100 border border-primary-200 dark:border-primary-200 rounded-lg p-4 mb-6">
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-primary-700 dark:text-primary-700"
                  >Destinatários:</span
                >
                <span class="font-medium text-primary-900 dark:text-primary-900">{{
                  recipientCount
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-primary-700 dark:text-primary-700"
                  >Inboxes selecionadas:</span
                >
                <span class="font-medium text-primary-900 dark:text-primary-900">{{
                  inboxCount
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-primary-700 dark:text-primary-700"
                  >Tipo de mensagem:</span
                >
                <span class="font-medium text-primary-900 dark:text-primary-900">{{
                  messageType
                }}</span>
              </div>
              <div v-if="estimatedTime" class="flex justify-between">
                <span class="text-primary-700 dark:text-primary-700"
                  >Tempo estimado:</span
                >
                <span class="font-medium text-primary-900 dark:text-primary-900">{{
                  estimatedTime
                }}</span>
              </div>
            </div>
          </div>

          <!-- Disconnected Inbox Warnings -->
          <div
            v-if="disconnectedInboxWarnings && disconnectedInboxWarnings.length > 0"
            class="bg-n-amber-2 dark:bg-n-amber-2 border border-n-amber-6 dark:border-n-amber-6 rounded-lg p-4 mb-6"
          >
            <div class="flex items-start gap-3">
              <i class="i-lucide-alert-triangle text-n-amber-11 dark:text-n-amber-11 text-xl mt-0.5 flex-shrink-0" />
              <div class="flex-1">
                <h4 class="font-medium text-n-amber-12 dark:text-n-amber-12 mb-2">
                  Aviso: Inboxes Desconectados
                </h4>
                <div class="space-y-2">
                  <div
                    v-for="warning in disconnectedInboxWarnings"
                    :key="warning.inboxId"
                    class="text-sm text-n-amber-11 dark:text-n-amber-11"
                  >
                    <span class="font-medium">{{ warning.inboxName }}</span> está desconectado - 
                    <span class="font-semibold">{{ warning.recipientCount }} mensage{{ warning.recipientCount === 1 ? 'm' : 'ns' }}</span> 
                    não {{ warning.recipientCount === 1 ? 'será enviada' : 'serão enviadas' }}
                  </div>
                </div>
                <p class="text-xs text-n-amber-10 dark:text-n-amber-10 mt-3">
                  Apenas contatos com inboxes conectados receberão mensagens.
                </p>
              </div>
            </div>
          </div>

          <!-- IA Automática Quick Action -->
          <div
            class="mb-6 p-4 bg-gradient-to-r from-n-teal-1 to-n-teal-2 dark:from-n-teal-1 dark:to-n-teal-2 border border-n-teal-6 dark:border-n-teal-6 rounded-lg"
          >
            <label
              class="block text-sm font-medium text-n-teal-12 dark:text-n-teal-12 mb-2"
            >
              <i class="i-lucide-bot text-n-teal-9 mr-2" />
              Ativar IA
            </label>

            <p class="text-xs text-n-teal-11 dark:text-n-teal-11 mb-3">
              Remove os contatos do Atendimento.
            </p>

            <!-- Segmented Control -->
            <div class="flex gap-1 p-1 bg-n-slate-3 dark:bg-n-slate-3 rounded-lg">
              <button
                v-for="option in atendimentoOptions"
                :key="option.id"
                type="button"
                :class="[
                  'flex-1 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2',
                  atendimentoAction === option.id
                    ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-4',
                ]"
                @click="atendimentoAction = option.id"
              >
                <i :class="option.icon" />
                <span>{{ option.label }}</span>
              </button>
            </div>

            <!-- Informative text based on selection -->
            <div
              v-if="atendimentoAction !== 'none'"
              class="mt-3 p-2 rounded-md text-xs"
              :class="
                atendimentoAction === 'remove'
                  ? 'bg-n-teal-3 text-n-teal-11 border border-n-teal-6'
                  : 'bg-n-amber-2 text-n-amber-11 border border-n-amber-6'
              "
            >
              <i class="i-lucide-info mr-1" />
              {{ atendimentoDescription }}
            </div>
          </div>

          <!-- Label Selection -->
          <div
            class="mb-6 p-4 bg-gradient-to-r from-n-iris-2 to-n-iris-3 dark:from-n-iris-2 dark:to-n-iris-3 border border-n-iris-6 dark:border-n-iris-6 rounded-lg"
          >
            <label
              class="block text-sm font-medium text-n-iris-12 dark:text-n-iris-12 mb-2"
            >
              <i class="i-lucide-tag text-n-iris-9 mr-2" />
              Adicionar etiquetas aos contatos (opcional)
            </label>

            <!-- Label Dropdown -->
            <div ref="dropdownContainer" class="relative">
              <button
                class="w-full px-4 py-3 bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 rounded-lg text-left flex items-center justify-between hover:border-n-iris-6 dark:hover:border-n-iris-6 transition-colors"
                @click="toggleDropdown"
              >
                <div
                  v-if="selectedLabels.length > 0"
                  class="flex items-center gap-2 flex-wrap"
                >
                  <span class="text-n-slate-12 dark:text-n-slate-12">
                    {{ selectedLabels.length }} etiqueta{{
                      selectedLabels.length > 1 ? 'es' : ''
                    }}
                    selecionado{{ selectedLabels.length > 1 ? 's' : '' }}
                  </span>
                  <div class="flex gap-1">
                    <span
                      v-for="label in selectedLabels.slice(0, 3)"
                      :key="label.id"
                      class="w-3 h-3 rounded-full"
                      :style="{ backgroundColor: label.color }"
                      :title="label.title"
                    />
                    <span
                      v-if="selectedLabels.length > 3"
                      class="text-xs text-n-slate-9 dark:text-n-slate-9"
                    >
                      +{{ selectedLabels.length - 3 }}
                    </span>
                  </div>
                </div>
                <span
v-else class="text-n-slate-9 dark:text-n-slate-9"
                  >Selecione os etiquetas</span
                >
                <i
                  :class="
                    showLabelDropdown
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                  class="text-n-slate-9 dark:text-n-slate-9"
                />
              </button>

              <!-- Dropdown Menu with fixed positioning -->
              <transition name="dropdown">
                <div
                  v-if="showLabelDropdown"
                  ref="dropdownRef"
                  class="absolute left-0 right-0 bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 rounded-lg shadow-lg z-50"
                  :class="
                    dropdownPosition === 'top'
                      ? 'bottom-full mb-1'
                      : 'top-full mt-1'
                  "
                  style="max-height: 400px"
                >
                  <!-- Search Input -->
                  <div
                    class="p-3 border-b border-n-slate-6 dark:border-n-slate-6"
                  >
                    <div class="relative">
                      <span
                        class="i-lucide-search absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2 left-2"
                      />
                      <input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Buscar ou criar etiqueta..."
                        class="h-10 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 !pl-8 !pr-10 border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm !px-3 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                        @keydown.enter="isNewLabel && createNewLabel()"
                      />
                      <button
                        v-if="searchQuery"
                        class="absolute right-2 top-1/2 -translate-y-1/2 text-n-slate-9 hover:text-n-slate-11"
                        @click="clearSearch"
                      >
                        <i class="i-lucide-x text-sm" />
                      </button>
                    </div>
                  </div>

                  <!-- Clear Selection -->
                  <button
                    v-if="selectedLabels.length > 0 && !searchQuery"
                    class="w-full px-4 py-2 text-left hover:bg-n-slate-2 dark:hover:bg-n-slate-2 transition-colors flex items-center gap-2 text-sm border-b border-n-slate-6 dark:border-n-slate-6"
                    @click="clearSelection"
                  >
                    <i class="i-lucide-x text-n-slate-9 dark:text-slate-400" />
                    <span class="text-n-slate-11 dark:text-n-slate-11"
                      >Limpar seleção</span
                    >
                  </button>

                  <div class="max-h-48 overflow-y-auto">
                    <!-- Create New Label Option when searching -->
                    <button
                      v-if="searchQuery && isNewLabel"
                      :disabled="isCreatingLabel"
                      class="w-full px-4 py-2 text-left hover:bg-n-iris-2 dark:hover:bg-n-iris-2 transition-colors flex items-center gap-2 text-sm border-b border-n-slate-6 dark:border-n-slate-6"
                      @click="createNewLabel"
                    >
                      <i
                        v-if="isCreatingLabel"
                        class="i-lucide-loader animate-spin text-n-iris-9"
                      />
                      <i v-else class="i-lucide-plus text-n-iris-9" />
                      <span class="text-n-iris-11 dark:text-n-iris-11">
                        Criar etiqueta "<strong>{{ searchQuery }}</strong
                        >"
                      </span>
                    </button>

                    <!-- Label Options -->
                    <label
                      v-for="label in filteredLabels"
                      :key="label.id"
                      class="w-full px-4 py-2 text-left hover:bg-n-slate-2 dark:hover:bg-n-slate-2 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        :checked="isLabelSelected(label)"
                        class="w-4 h-4 text-n-iris-9 bg-white border-n-slate-3 rounded focus:ring-n-iris-9 focus:ring-2"
                        @change="toggleLabel(label)"
                      />
                      <span
                        class="w-3 h-3 rounded-full flex-shrink-0"
                        :style="{ backgroundColor: label.color }"
                      />
                      <span
                        class="text-n-slate-12 dark:text-n-slate-12 text-sm"
                        >{{ label.title }}</span
                      >
                    </label>

                    <!-- No Results Message -->
                    <div
                      v-if="
                        searchQuery &&
                        filteredLabels.length === 0 &&
                        !isNewLabel
                      "
                      class="px-4 py-3 text-center text-sm text-n-slate-9 dark:text-n-slate-9"
                    >
                      Nenhum etiqueta encontrado
                    </div>

                    <!-- No Labels Message -->
                    <div
                      v-if="!searchQuery && availableLabels.length === 0"
                      class="px-4 py-3 text-center text-sm text-n-slate-9 dark:text-n-slate-9"
                    >
                      Nenhum etiqueta disponível
                    </div>
                  </div>

                  <!-- Create New Label Button - Always visible at bottom -->
                  <div class="border-t border-n-slate-6 dark:border-n-slate-6">
                    <button
                      class="w-full px-4 py-3 text-left hover:bg-n-iris-2 dark:hover:bg-n-iris-2 transition-colors flex items-center gap-2 text-sm"
                      @click="showCreateLabelDialog"
                    >
                      <i class="i-lucide-plus-circle text-n-iris-9" />
                      <span
                        class="text-n-iris-11 dark:text-n-iris-11 font-medium"
                        >Criar nova etiqueta</span
                      >
                    </button>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Warning for Label Application -->
          <div
            v-if="selectedLabels.length > 0"
            class="bg-gradient-to-r from-n-amber-2 to-n-amber-3 dark:from-n-amber-2 dark:to-n-amber-3 border border-n-amber-6 dark:border-n-amber-6 rounded-lg p-3 mb-6"
          >
            <div class="flex items-start gap-2">
              <i
                class="i-lucide-info text-n-amber-9 dark:text-n-amber-9 text-sm mt-0.5"
              />
              <div class="text-sm text-n-amber-11 dark:text-n-amber-11">
                <p v-if="selectedLabels.length === 1">
                  A etiqueta <strong>{{ selectedLabels[0].title }}</strong> será
                  adicionado a todos os {{ recipientCount }} contatos após o
                  envio.
                </p>
                <div v-else>
                  <p class="mb-1">
                    Os seguintes etiquetas serão adicionados a todos os
                    {{ recipientCount }} contatos após o envio:
                  </p>
                  <ul class="list-disc list-inside ml-2">
                    <li
                      v-for="label in selectedLabels"
                      :key="label.id"
                      class="flex items-center gap-2"
                    >
                      <span
                        class="w-2 h-2 rounded-full inline-block"
                        :style="{ backgroundColor: label.color }"
                      />
                      <strong>{{ label.title }}</strong>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Remove Labels Selection -->
          <div
            class="mb-6 p-4 bg-gradient-to-r from-n-ruby-1 to-n-ruby-2 dark:from-n-ruby-1 dark:to-n-ruby-2 border border-n-ruby-6 dark:border-n-ruby-6 rounded-lg"
          >
            <label
              class="block text-sm font-medium text-n-ruby-12 dark:text-n-ruby-12 mb-2"
            >
              <i class="i-lucide-tag-off text-n-ruby-9 mr-2" />
              Remover etiquetas dos contatos (opcional)
            </label>

            <div ref="removeDropdownContainer" class="relative">
              <button
                class="w-full px-4 py-3 bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 rounded-lg text-left flex items-center justify-between hover:border-n-ruby-6 dark:hover:border-n-ruby-6 transition-colors"
                @click="toggleRemoveDropdown"
              >
                <div
                  v-if="selectedRemoveLabels.length > 0"
                  class="flex items-center gap-2 flex-wrap"
                >
                  <span class="text-n-slate-12 dark:text-n-slate-12">
                    {{ selectedRemoveLabels.length }} etiqueta{{
                      selectedRemoveLabels.length > 1 ? 's' : ''
                    }}
                    para remover
                  </span>
                  <div class="flex gap-1">
                    <span
                      v-for="label in selectedRemoveLabels.slice(0, 3)"
                      :key="label.id"
                      class="w-3 h-3 rounded-full"
                      :style="{ backgroundColor: label.color }"
                      :title="label.title"
                    />
                    <span
                      v-if="selectedRemoveLabels.length > 3"
                      class="text-xs text-n-slate-9 dark:text-n-slate-9"
                    >
                      +{{ selectedRemoveLabels.length - 3 }}
                    </span>
                  </div>
                </div>
                <span
                  v-else
                  class="text-n-slate-9 dark:text-n-slate-9"
                  >Selecione etiquetas para remover</span
                >
                <i
                  :class="
                    showRemoveLabelDropdown
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                  class="text-n-slate-9 dark:text-n-slate-9"
                />
              </button>

              <transition name="dropdown">
                <div
                  v-if="showRemoveLabelDropdown"
                  class="absolute left-0 right-0 bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 rounded-lg shadow-lg z-50"
                  :class="
                    removeDropdownPosition === 'top'
                      ? 'bottom-full mb-1'
                      : 'top-full mt-1'
                  "
                  style="max-height: 400px"
                >
                  <!-- Search Input -->
                  <div
                    class="p-3 border-b border-n-slate-6 dark:border-n-slate-6"
                  >
                    <div class="relative">
                      <span
                        class="i-lucide-search absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2 left-2"
                      />
                      <input
                        v-model="removeSearchQuery"
                        type="text"
                        placeholder="Buscar etiqueta para remover..."
                        class="h-10 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 !pl-8 !pr-10 border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm !px-3 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                      />
                      <button
                        v-if="removeSearchQuery"
                        class="absolute right-2 top-1/2 -translate-y-1/2 text-n-slate-9 dark:text-n-slate-9 hover:text-n-slate-11 dark:hover:text-n-slate-11"
                        @click="clearRemoveSearch"
                      >
                        <i class="i-lucide-x text-sm" />
                      </button>
                    </div>
                  </div>

                  <!-- Clear Selection -->
                  <button
                    v-if="selectedRemoveLabels.length > 0 && !removeSearchQuery"
                    class="w-full px-4 py-2 text-left hover:bg-n-slate-2 dark:hover:bg-n-slate-2 transition-colors flex items-center gap-2 text-sm border-b border-n-slate-6 dark:border-n-slate-6"
                    @click="clearRemoveSelection"
                  >
                    <i class="i-lucide-x text-n-slate-9 dark:text-slate-400" />
                    <span class="text-n-slate-11 dark:text-n-slate-11"
                      >Limpar seleção</span
                    >
                  </button>

                  <div class="max-h-48 overflow-y-auto">
                    <!-- Label Options -->
                    <label
                      v-for="label in filteredRemoveLabels"
                      :key="label.id"
                      class="w-full px-4 py-2 text-left hover:bg-n-slate-2 dark:hover:bg-n-slate-2 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        :checked="isRemoveLabelSelected(label)"
                        class="w-4 h-4 text-n-ruby-9 bg-white border-n-slate-3 rounded focus:ring-n-ruby-9 focus:ring-2"
                        @change="toggleRemoveLabel(label)"
                      />
                      <span
                        class="w-3 h-3 rounded-full flex-shrink-0"
                        :style="{ backgroundColor: label.color }"
                      />
                      <span
                        class="text-n-slate-12 dark:text-n-slate-12 text-sm"
                        >{{ label.title }}</span
                      >
                    </label>

                    <!-- No Results Message -->
                    <div
                      v-if="
                        removeSearchQuery &&
                        filteredRemoveLabels.length === 0
                      "
                      class="px-4 py-3 text-center text-sm text-n-slate-9 dark:text-n-slate-9"
                    >
                      Nenhum etiqueta encontrada
                    </div>

                    <!-- No Labels Message -->
                    <div
                      v-if="!removeSearchQuery && availableLabels.length === 0"
                      class="px-4 py-3 text-center text-sm text-n-slate-9 dark:text-n-slate-9"
                    >
                      Nenhum etiqueta disponível
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-3">
            <button
              class="flex-1 px-4 py-3 bg-n-slate-4 hover:bg-n-slate-5 dark:bg-n-slate-4 dark:hover:bg-n-slate-5 text-n-slate-12 dark:text-n-slate-12 border border-n-slate-6 dark:border-n-slate-6 rounded-lg font-medium transition-colors duration-200"
              @click="$emit('close')"
            >
              Cancelar
            </button>
            <button
              :disabled="isConfirming"
              class="flex-1 px-4 py-3 bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-500 dark:to-primary-600 hover:from-primary-600 hover:to-primary-700 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
              @click="confirmBroadcast"
            >
              <i v-if="isConfirming" class="i-lucide-loader animate-spin" />
              <i v-else class="i-lucide-send" />
              <span>{{
                isConfirming ? 'Enviando...' : 'Enviar'
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
